import { createSelector, compose, combineReducers } from '@reduxjs/toolkit';

import wallPreviewsAdapter from './wall_preview_rooms.adapter';
import slice from './wall_preview_rooms.slice';

import { notNull, roomIsUserRoom } from 'utils/type_guards';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const defaultSelectors = wallPreviewsAdapter.getSelectors();

export const selectWhichWallPreview = (state: LocalState) => state.whichWallPreview;
export const selectEditRoomDrafts = (state: LocalState) => state.editRoomDrafts;
export const selectEditRoomDraftById = (state: LocalState, id: number) => state.editRoomDrafts[id];

export const selectUserRooms = createSelector(
  [compose(defaultSelectors.selectAll, localState)],
  (rooms) => rooms.filter((room) => room.type === 'user')
);

export const selectDraftRooms = createSelector(
  [compose(selectEditRoomDrafts, localState), compose(defaultSelectors.selectEntities, localState)],
  (drafts, roomsById) => {
    const draftIds = Object.keys(drafts);
    return draftIds
      .map((id) => roomsById[id])
      .filter(notNull)
      .filter(roomIsUserRoom);
  }
);

export default {
  ...wallPreviewsAdapter.getSelectors(localState),
  whichWallPreview: compose(selectWhichWallPreview, localState),
  editRoomDrafts: compose(selectEditRoomDrafts, localState),
  editRoomDraftById: (state: GlobalState, id: number) =>
    selectEditRoomDraftById(localState(state), id),
  // Following are already globalized
  draftRooms: selectDraftRooms,
  userRooms: selectUserRooms,
};
